import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const AvatarImage = styled.div`
  width: ${props => props.size || 100}px;
  height: ${props => props.size || 100}px;
  border-radius: 50%;
  background: ${props => props.hasImage ? `url(${props.src})` : '#F1AF59'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => (props.size || 100) / 2.5}px;
  font-weight: 600;
  color: white;
  cursor: ${props => props.editable ? 'pointer' : 'default'};
  transition: opacity 0.2s;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    opacity: ${props => props.editable ? 0.8 : 1};
  }
`;

const UploadOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;

  ${AvatarContainer}:hover & {
    opacity: 1;
  }
`;

const UploadIcon = styled.span`
  color: white;
  font-size: 24px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 8px;
  text-align: center;
`;

/**
 * Avatar upload component with preview and upload functionality.
 *
 * @param {string} avatarUrl - Current avatar URL
 * @param {string} name - User's name (for initials fallback)
 * @param {number} size - Avatar size in pixels
 * @param {boolean} editable - Whether the avatar can be edited
 * @param {function} onUpload - Callback function when upload completes
 * @param {function} uploadFunction - Function to upload the file
 */
const AvatarUpload = ({
  avatarUrl,
  name = '',
  size = 100,
  editable = true,
  onUpload,
  uploadFunction
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  // Get initials from name
  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const names = fullName.trim().split(' ');
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const handleClick = () => {
    if (editable && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setIsLoading(true);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    try {
      if (uploadFunction) {
        const newAvatarUrl = await uploadFunction(file);
        if (onUpload) {
          onUpload(newAvatarUrl);
        }
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError('Failed to upload avatar. Please try again.');
      setPreviewUrl('');
    } finally {
      setIsLoading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const displayUrl = previewUrl || avatarUrl;
  const hasImage = !!displayUrl;

  return (
    <div>
      <AvatarContainer onClick={handleClick}>
        <AvatarImage
          size={size}
          hasImage={hasImage}
          src={displayUrl}
          editable={editable}
        >
          {!hasImage && getInitials(name)}
        </AvatarImage>
        {editable && !isLoading && (
          <UploadOverlay>
            <UploadIcon>📷</UploadIcon>
          </UploadOverlay>
        )}
        {isLoading && (
          <UploadOverlay style={{ opacity: 1 }}>
            <LoadingSpinner />
          </UploadOverlay>
        )}
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
        />
      </AvatarContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

export default AvatarUpload;
